import axios from 'axios';

import { IBundlesRepository, RequestBundlePayload, RequestBundleResponse } from '../../../domain/bundles';
import { ServiceType } from '../../../domain/common';

import { BUNDLES_REPOSITORY_ENDPOINTS } from './bundles.repository.config';
import { RecommendableBundle } from '../../../domain/bundles/recommendable-bundle';
import { BundleDisclaimerResponse } from '../../../domain/bundles/disclaimer-bundle';

/* === STATIC DEPENDENCIES ============================================================ */
/**
 * Generate axios instance with headers
 */
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

export class BundlesRepository implements IBundlesRepository {
  /* === PROPERTIES =================================================================== */
  private _endpoints: typeof BUNDLES_REPOSITORY_ENDPOINTS;

  /* === CONSTRUCTOR ================================================================== */
  constructor() {
    this._endpoints = { ...BUNDLES_REPOSITORY_ENDPOINTS };
  }

  /* === METHODS ====================================================================== */
  async requestBundle(payload: RequestBundlePayload): Promise<RequestBundleResponse> {
    const response = await axiosInstance.post(this._endpoints.requestBundle, { ...payload, cart: this.mapCartToCartOfRequestBundle(payload.cart) });

    return {
      applicable: response.data.applicable,
      bundle: { ...response.data.bundle, totalDiscount: response.data.totalDiscount },
    };
  }

  async requestRecommendation(payload: RequestBundlePayload): Promise<RecommendableBundle> {
    const response = await axiosInstance.post(this._endpoints.requestRecommendation, { ...payload, cart: this.mapCartToCartOfRequestBundle(payload.cart) });
    return {
      applicable: response.data.applicable,
      bundle: { ...response.data.bundle, totalDiscount: response.data?.bundle?.totalDiscount },
      recommendation: response.data.recommendation,
      messages: response.data.messages,
    };
  }

  async requestBundleDisclaimer(location: RequestBundlePayload['location']): Promise<BundleDisclaimerResponse> {
    const response = await axiosInstance.post(this._endpoints.requestBundleDisclaimer, { location });
    return response.data;
  }

  /**
   * {@link requestBundle} MAPPER.
   */
  private mapCartToCartOfRequestBundle(cart: Record<string, any>[]) {
    return cart.map((service) => {
      switch (service.item.type) {
        case ServiceType.Ticket:
          let returnDate = service.item.returnDate;
          if (!returnDate) {
            returnDate = new Date(service.item.departureDate);
          }
          /**
           * Calculate the ticket total, verifying the NonDiscountAmount state.
           *
           * In case NonDiscountAmount has value, that means a bundle is applied,
           * and to request a bundle is required to send the amount without discount.
           *
           * With bundle: NonDiscountAmount (Original Amount) * NuProdQuant.
           * No Bundle  : total (In this scenario it has not been modified).
           */
          const ticketTotal = service.item.NonDiscountAmount ? service.item.NonDiscountAmount * service.item.tixProd.NuProdQuant : service.item.total;

          return {
            type: service.item.type,
            startDate: new Date(service.item.departureDate).toISOString().substring(0, 10),
            endDate: new Date(returnDate).toISOString().substring(0, 10),
            tripType: service.item.tripType,
            quantity: service.item.quantity,
            amount /** Total (Amount * NuProdQuant) */: ticketTotal,
          };
        case ServiceType.Parking:
          /**
           * Calculate the parking total, verifying the NonDiscountAmount state.
           *
           * In case NonDiscountAmount has value, that means a bundle is applied,
           * and to request a bundle is required to send the amount without discount.
           *
           * With bundle: NonDiscountAmount (Original Amount).
           * No Bundle  : Amount (In this scenario it has not been modified).
           */
          const parkingTotal = service.item.NonDiscountAmount ? service.item.NonDiscountAmount : service.item.Amount;

          return {
            type: service.item.type,
            startDate: service.item.start_time.substring(0, 10),
            endDate: service.item.end_time.substring(0, 10),
            lot: service.item.location,
            amount: parkingTotal,
            days: service.item.days,
          };
        default:
          return {
            type: service.item.type,
          };
      }
    });
  }
}
